import { useState } from 'react';
import {
  ChevronDown, ChevronLeft, ChevronRight, Layers, Plus, Repeat, RotateCcw, Timer, Trash2, TrendingUp, X,
} from 'lucide-react';
import { createDefaultPlan, createExercise, cycleIndexForDate, dateKey, todayKey, workoutToday } from './state';
import { activeProgram } from './logic/progression';
import { formatClock, restSecondsFor } from './rest';
import { MAX_ALTS } from './swaps';
import { loadStep } from './setLog';
import { RULES, RULE_LABEL, normaliseRepRange, progressionOf, repRangePatch } from './targets';
import { DraftNumberInput, Segmented } from './inputs';
import { ProgramSheet } from './ProgramSheet';
import { FONT_BODY, FONT_MONO } from './theme';
import { Button, Card, Input, NumberInput, TypeDot } from './ui';
import { ExercisePicker, makeCustomExerciseHandlers } from './ExercisePicker';
import { renameExerciseEverywhere, renamePreview } from './logic/names';
import { anchorFor, postponedAnchor, setCycleAnchor } from './session';

/* A patch where null or [] removes an optional field. Deleted, not set to
 * undefined, which the sync hash would read as null. */
function withPatch(ex, patch) {
  const out = { ...ex, ...patch };
  Object.keys(patch).forEach(k => {
    const v = patch[k];
    if (v === null || v === undefined || (Array.isArray(v) && !v.length)) delete out[k];
  });
  return out;
}

const iconBtn = t => ({
  width: 40, height: 40, flexShrink: 0, background: 'transparent', border: 'none', cursor: 'pointer', padding: 0,
  color: t.textFaint, display: 'flex', alignItems: 'center', justifyContent: 'center',
});

const labelStyle = t => ({ fontSize: 10, color: t.textFaint, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase' });

const RULE_HELP = {
  double: (ex, step) => `Add reps up to the top of the range, then +${step} kg and back to the bottom.`,
  linear: (ex, step) => `Same reps every set; +${step} kg once every set hits them.`,
  manual: () => 'No suggestion: the ghosts are your last session.',
};

/* Progression, setup, program, rest time and "machine taken" alternatives
 * of one plan exercise. */
function ExerciseDetails({ t, ex, step, block, onPatch, onRest, onRemoveAlt, onAddAlt, onProgram }) {
  const auto = restSecondsFor({ ...ex, restSec: null });
  const alts = ex.alts || [];
  const rule = progressionOf(ex);
  return (
    <div style={{ padding: '4px 0 12px', display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ ...labelStyle(t), width: 84, paddingTop: 14 }}>Progression</span>
        <div style={{ flex: '1 1 220px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
          <Segmented t={t} ariaLabel="Progression" value={rule} onChange={v => onPatch({ progression: v })}
            options={RULES.map(r => ({ value: r, label: RULE_LABEL[r] }))} />
          <span style={{ fontSize: 11, color: t.textFaint }}>{RULE_HELP[rule](ex, step)}</span>
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ ...labelStyle(t), width: 84 }}>Setup</span>
        <Input t={t} value={ex.setup || ''} maxLength={80} placeholder="Seat 4, handles high"
          onChange={e => onPatch({ setup: e.target.value.trim() ? e.target.value : null })}
          style={{ flex: '1 1 200px', width: 'auto', minWidth: 0, height: 40 }} />
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ ...labelStyle(t), width: 84 }}>Program</span>
        <span style={{ flex: '1 1 120px', minWidth: 0, fontSize: 13, color: block ? t.text : t.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {block ? block.name : 'None, plan progression'}
        </span>
        <Button t={t} size="sm" onClick={onProgram} style={{ height: 40 }}>
          <Layers size={13} /> {block ? 'Program' : 'Start program'}
        </Button>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ ...labelStyle(t), width: 84 }}>Rest</span>
        <div style={{ width: 110 }}>
          <NumberInput t={t} value={ex.restSec ?? null} step={15} min={0} suffix="s" placeholder={auto}
            onChange={v => onRest(v > 0 ? Math.round(v) : null)} style={{ height: 40 }} />
        </div>
        <span style={{ fontSize: 11, color: t.textFaint }}>
          {ex.restSec ? `${formatClock(ex.restSec)} after each set` : `Empty = auto (${formatClock(auto)})`}
        </span>
      </div>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ ...labelStyle(t), width: 84, paddingTop: 12 }}>Alternatives</span>
        <div style={{ flex: '1 1 200px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {alts.map(name => (
            <div key={name} style={{
              display: 'flex', alignItems: 'center', gap: 6, minHeight: 40, paddingLeft: 12, borderRadius: 8,
              background: t.surfaceAlt, border: `1px solid ${t.cardBorder}`,
            }}>
              <span style={{ flex: 1, minWidth: 0, fontSize: 13, color: t.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</span>
              <button onClick={() => onRemoveAlt(name)} aria-label={`Remove alternative ${name}`} style={{
                width: 40, height: 40, flexShrink: 0, border: 'none', background: 'transparent', color: t.textFaint,
                cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}><X size={14} /></button>
            </div>
          ))}
          {alts.length < MAX_ALTS && (
            <Button t={t} size="sm" onClick={onAddAlt} style={{ alignSelf: 'flex-start', height: 40 }}>
              <Plus size={13} /> Add alternative
            </Button>
          )}
          <span style={{ fontSize: 11, color: t.textFaint }}>
            Shown first when you tap Swap because the machine is taken.
          </span>
        </div>
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────────── */
/*  Plan tab                                                                  */
/* ────────────────────────────────────────────────────────────────────────── */

export function PlanTab({ t, state, setState, isDark }) {
  const [editingDay, setEditingDay] = useState(null);
  const [picker, setPicker] = useState(null); // { dayIdx, exId?, alt? }  — exId set = rename existing, alt = add alternative
  const [detailsOpen, setDetailsOpen] = useState(null); // exercise id with rest/alternatives open
  const [programFor, setProgramFor] = useState(null); // { exercise, planExercise, planDayId }
  const [renameAsk, setRenameAsk] = useState(null); // { dayIdx, exId, from, to, sessions }
  const customEx = makeCustomExerciseHandlers(setState);
  const today = todayKey();

  const updateDay = (idx, patch) => {
    setState(prev => ({ ...prev, plan: prev.plan.map((d, i) => i === idx ? { ...d, ...patch } : d) }));
  };
  const addExerciseWithName = (idx, name) => {
    setState(prev => ({ ...prev, plan: prev.plan.map((d, i) => i === idx
      ? { ...d, exercises: [...d.exercises, createExercise(name, 3, 10)] } : d) }));
  };
  const updateExercise = (dayIdx, exId, patch) => {
    setState(prev => {
      const ex = prev.plan[dayIdx]?.exercises.find(e => e.id === exId);
      const p = ex && (typeof patch === 'function' ? patch(ex) : patch);
      if (!p || !Object.keys(p).length) return prev; // nothing to change: no save
      return { ...prev, plan: prev.plan.map((d, i) => i === dayIdx
        ? { ...d, exercises: d.exercises.map(e => e.id === exId ? withPatch(e, p) : e) } : d) };
    });
  };
  // Range ends commit as typed; order and equal ends are tidied on blur.
  const setRepEnd = (dayIdx, exId, end, v) => updateExercise(dayIdx, exId, e => repRangePatch(e, end, v));
  const tidyRange = (dayIdx, exId, end) => updateExercise(dayIdx, exId, e => normaliseRepRange(e, end));
  const addAlt = (dayIdx, exId, name) => updateExercise(dayIdx, exId, e => ({
    alts: [...(e.alts || []), name].filter((n, i, all) => n !== e.name && all.indexOf(n) === i).slice(0, MAX_ALTS),
  }));
  const removeAlt = (dayIdx, exId, name) => updateExercise(dayIdx, exId, e => ({ alts: (e.alts || []).filter(n => n !== name) }));
  const removeExercise = (dayIdx, exId) => {
    setState(prev => ({ ...prev, plan: prev.plan.map((d, i) => i === dayIdx
      ? { ...d, exercises: d.exercises.filter(e => e.id !== exId) } : d) }));
  };
  const moveExercise = (dayIdx, exId, dir) => {
    setState(prev => ({ ...prev, plan: prev.plan.map((d, i) => {
      if (i !== dayIdx) return d;
      const idx = d.exercises.findIndex(e => e.id === exId);
      const next = idx + dir;
      if (idx < 0 || next < 0 || next >= d.exercises.length) return d;
      const ex = [...d.exercises]; [ex[idx], ex[next]] = [ex[next], ex[idx]];
      return { ...d, exercises: ex };
    })}));
  };

  const resetToDefaultPlan = () => {
    const ok = window.confirm(
      'Replace your current plan with the built-in default?\n\n' +
      'Your logs, swaps, set-overrides, body metrics and nutrition data stay intact — only the plan template is overwritten.'
    );
    if (!ok) return;
    setState(prev => ({ ...prev, plan: createDefaultPlan() }));
  };

  // Shift the cycle anchor so today lands on the chosen plan index.
  // Anchor = today − planIdx days (anchor day = day 0 of the cycle).
  // Both go through setCycleAnchor, which first stamps every trained date
  // with its current day, so the past keeps its labels.
  const setTodayPlanIdx = (planIdx) => setState(prev => setCycleAnchor(prev, anchorFor(dateKey(workoutToday()), planIdx)));
  const postpone = () => setState(prev => setCycleAnchor(prev, postponedAnchor(prev)));

  const todayPlanIdx = cycleIndexForDate(workoutToday(), state.plan.length, state.cycleAnchor);
  const afterPostpone = state.plan[cycleIndexForDate(workoutToday(), state.plan.length, postponedAnchor(state))];

  /* Renaming a plan exercise whose name has history asks what it is: the same
   * movement (move its history to the new name) or a new exercise. */
  const renameExercise = (dayIdx, exId, name) => {
    const old = state.plan[dayIdx]?.exercises.find(e => e.id === exId)?.name;
    const sessions = old && old !== name ? renamePreview(state, old, name).fromSessions : 0;
    if (sessions > 0) setRenameAsk({ dayIdx, exId, from: old, to: name, sessions });
    else updateExercise(dayIdx, exId, { name });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 4, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 13, color: t.textMuted }}>
          {state.plan.length}-day rotating cycle. Rotation anchor:&nbsp;
          <span style={{ fontFamily: FONT_MONO, color: t.text }}>{state.cycleAnchor}</span>
        </div>
        <Button t={t} size="sm" variant="ghost" onClick={resetToDefaultPlan}>
          <RotateCcw size={13} /> Reset to default plan
        </Button>
      </div>

      <Card t={t} padding={16}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div className="fit-today-select" style={{ flex: '1 1 240px', minWidth: 0 }}>
            <div style={{ fontSize: 10, color: t.textFaint, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 4 }}>
              Today is
            </div>
            <select value={todayPlanIdx}
              onChange={e => setTodayPlanIdx(Number(e.target.value))}
              style={{
                background: t.inputBg, color: t.text, border: `1px solid ${t.inputBorder}`,
                borderRadius: 8, padding: '8px 12px', fontSize: 13, fontFamily: FONT_BODY,
                outline: 'none', minWidth: 240, width: '100%',
              }}>
              {state.plan.map((d, i) => (
                <option key={d.id} value={i}>
                  {i + 1}. {d.name}{d.focus ? ` — ${d.focus}` : ''}
                </option>
              ))}
            </select>
          </div>
          <Button t={t} size="sm" onClick={postpone} style={{ minHeight: 40 }}
            title={afterPostpone ? `Today becomes ${afterPostpone.name}; today's day moves to tomorrow` : undefined}>
            Postpone 1 day{afterPostpone ? ` · today: ${afterPostpone.name}` : ''}
          </Button>
          <div style={{ fontSize: 11, color: t.textFaint, maxWidth: 320, lineHeight: 1.4 }}>
            Shifts the whole rotation. Past sessions keep the day they were trained as.
          </div>
        </div>
      </Card>

      {state.plan.map((day, idx) => {
        const expanded = editingDay === day.id;
        return (
          <Card key={day.id} t={t} padding={0} style={{ overflow: 'hidden' }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 12, padding: '14px 18px',
              cursor: 'pointer',
            }} onClick={() => setEditingDay(expanded ? null : day.id)}>
              <span style={{
                fontSize: 11, fontFamily: FONT_MONO, color: t.textFaint, width: 22, textAlign: 'center', fontWeight: 700,
              }}>{idx + 1}</span>
              <TypeDot type={day.type} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 15, fontWeight: 600, color: t.text }}>{day.name}</div>
                <div style={{ fontSize: 12, color: t.textMuted }}>
                  {day.isRest ? 'Rest day' : `${day.exercises.length} exercises · ${day.focus || day.type}`}
                </div>
              </div>
              <ChevronDown size={16} color={t.textFaint}
                style={{ transform: expanded ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.2s' }} />
            </div>

            {expanded && (
              <div style={{ padding: '0 18px 16px', borderTop: `1px solid ${t.divider}` }}>
                <div className="fit-plan-day-edit" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 120px', gap: 10, paddingTop: 14, marginBottom: 12 }}>
                  <div>
                    <div style={{ fontSize: 10, color: t.textFaint, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: 4 }}>Name</div>
                    <Input t={t} value={day.name} onChange={e => updateDay(idx, { name: e.target.value })} />
                  </div>
                  <div>
                    <div style={{ fontSize: 10, color: t.textFaint, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: 4 }}>Focus</div>
                    <Input t={t} value={day.focus || ''} onChange={e => updateDay(idx, { focus: e.target.value })} />
                  </div>
                  <div>
                    <div style={{ fontSize: 10, color: t.textFaint, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: 4 }}>Type</div>
                    <select value={day.type}
                      onChange={e => updateDay(idx, { type: e.target.value, isRest: e.target.value === 'rest' })}
                      style={{
                        width: '100%', background: t.inputBg, color: t.text, border: `1px solid ${t.inputBorder}`,
                        borderRadius: 8, padding: '8px 10px', fontSize: 13, fontFamily: FONT_BODY, outline: 'none',
                      }}>
                      {['push', 'pull', 'legs', 'upper', 'lower', 'full', 'rest'].map(x => (
                        <option key={x} value={x}>{x}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {!day.isRest && (
                  <>
                    {day.exercises.map(ex => {
                      const step = loadStep(ex.name, ex, state.logs, state.exerciseMeta);
                      const block = activeProgram(state.programs, ex.name, today, { planDayId: day.id, plan: state.plan });
                      const lo = ex.repMin ?? ex.reps;
                      const hi = ex.repMax ?? ex.reps;
                      const open = detailsOpen === ex.id;
                      return (
                        <div key={ex.id} style={{ borderTop: `1px solid ${t.divider}`, paddingTop: 8 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <button onClick={() => setPicker({ dayIdx: idx, exId: ex.id })} style={{
                              flex: 1, minWidth: 0, height: 40, background: t.inputBg, color: t.text, border: `1px solid ${t.inputBorder}`,
                              borderRadius: 8, padding: '0 12px', fontSize: 13, fontFamily: FONT_BODY, fontWeight: 600,
                              textAlign: 'left', cursor: 'pointer', overflow: 'hidden', textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap', marginRight: 4,
                            }}>{ex.name}</button>
                            <button aria-label="Move up" onClick={() => moveExercise(idx, ex.id, -1)} style={iconBtn(t)}>
                              <ChevronLeft size={15} style={{ transform: 'rotate(90deg)' }} />
                            </button>
                            <button aria-label="Move down" onClick={() => moveExercise(idx, ex.id, 1)} style={iconBtn(t)}>
                              <ChevronRight size={15} style={{ transform: 'rotate(90deg)' }} />
                            </button>
                            <button aria-label={`Remove ${ex.name}`} onClick={() => removeExercise(idx, ex.id)} style={{ ...iconBtn(t), color: t.danger }}>
                              <Trash2 size={15} />
                            </button>
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: '52px minmax(0, 112px) 78px', gap: 10, paddingTop: 6, alignItems: 'end' }}>
                            <label style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                              <span style={labelStyle(t)}>Sets</span>
                              <DraftNumberInput t={t} ariaLabel={`${ex.name} sets`} integer min={1} max={20} value={ex.sets}
                                onChange={v => updateExercise(idx, ex.id, { sets: v })} />
                            </label>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                              <span style={labelStyle(t)}>Reps</span>
                              <div style={{ display: 'grid', gridTemplateColumns: '1fr 10px 1fr', alignItems: 'center', gap: 2 }}>
                                <DraftNumberInput t={t} ariaLabel={`${ex.name} lowest reps`} integer min={1} max={100} value={lo}
                                  onChange={v => setRepEnd(idx, ex.id, 'min', v)} onBlur={() => tidyRange(idx, ex.id, 'min')} />
                                <span style={{ textAlign: 'center', color: t.textFaint, fontSize: 12 }}>–</span>
                                <DraftNumberInput t={t} ariaLabel={`${ex.name} highest reps`} integer min={1} max={100} value={hi}
                                  onChange={v => setRepEnd(idx, ex.id, 'max', v)} onBlur={() => tidyRange(idx, ex.id, 'max')} />
                              </div>
                            </div>
                            <label style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                              <span style={labelStyle(t)}>Step</span>
                              <DraftNumberInput t={t} ariaLabel={`${ex.name} load step`} allowEmpty min={0.25} max={50} suffix="kg"
                                value={ex.increment ?? null} placeholder={step}
                                onChange={v => updateExercise(idx, ex.id, { increment: v })} />
                            </label>
                          </div>
                          <button onClick={() => setDetailsOpen(open ? null : ex.id)} aria-expanded={open}
                            style={{
                              width: '100%', minHeight: 40, padding: '0 2px', display: 'flex', alignItems: 'center', gap: 6,
                              background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left',
                              color: t.textMuted, fontFamily: FONT_BODY, fontSize: 12,
                            }}>
                            {block ? <Layers size={12} color={t.accent} style={{ flexShrink: 0 }} /> : <TrendingUp size={12} color={t.textFaint} style={{ flexShrink: 0 }} />}
                            <span style={{ flexShrink: 0, color: block ? t.accent : t.textMuted, fontWeight: block ? 600 : 400 }}>
                              {block ? block.name : RULE_LABEL[progressionOf(ex)]}
                            </span>
                            <span style={{ color: t.textFaint }}>·</span>
                            <Timer size={12} color={t.textFaint} style={{ flexShrink: 0 }} />
                            <span style={{ fontFamily: FONT_MONO, flexShrink: 0, color: ex.restSec ? t.text : t.textMuted }}>
                              {formatClock(restSecondsFor(ex))}{ex.restSec ? '' : ' auto'}
                            </span>
                            <span style={{ color: t.textFaint }}>·</span>
                            <Repeat size={12} color={t.textFaint} style={{ flexShrink: 0 }} />
                            <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {ex.setup || (ex.alts?.length ? ex.alts.join(', ') : 'No alternatives')}
                            </span>
                            <ChevronDown size={14} color={t.textFaint}
                              style={{ flexShrink: 0, transform: open ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.2s' }} />
                          </button>
                          {open && (
                            <ExerciseDetails t={t} ex={ex} step={step} block={block}
                              onPatch={patch => updateExercise(idx, ex.id, patch)}
                              onRest={sec => updateExercise(idx, ex.id, { restSec: sec })}
                              onRemoveAlt={name => removeAlt(idx, ex.id, name)}
                              onAddAlt={() => setPicker({ dayIdx: idx, exId: ex.id, alt: true })}
                              onProgram={() => setProgramFor({ exercise: ex.name, planExercise: ex, planDayId: day.id })} />
                          )}
                        </div>
                      );
                    })}
                    <div style={{ paddingTop: 12 }}>
                      <Button t={t} size="sm" onClick={() => setPicker({ dayIdx: idx })}>
                        <Plus size={13} /> Add exercise
                      </Button>
                    </div>
                  </>
                )}
              </div>
            )}
          </Card>
        );
      })}

      {programFor && (
        <ProgramSheet t={t} state={state} setState={setState} date={today} {...programFor}
          onClose={() => setProgramFor(null)} />
      )}

      {renameAsk && (
        <div onClick={() => setRenameAsk(null)} style={{
          position: 'fixed', inset: 0, zIndex: 100, background: 'rgba(0,0,0,0.55)',
          display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
        }}>
          <div role="dialog" aria-label="Rename exercise" onClick={e => e.stopPropagation()} style={{
            background: t.cardBg, borderTopLeftRadius: 16, borderTopRightRadius: 16, width: '100%', maxWidth: 560,
            padding: '18px 18px calc(18px + env(safe-area-inset-bottom))', display: 'flex', flexDirection: 'column', gap: 10,
            fontFamily: FONT_BODY,
          }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: t.text }}>
              {renameAsk.from} → {renameAsk.to}
            </div>
            <div style={{ fontSize: 13, color: t.textMuted, lineHeight: 1.45 }}>
              {renameAsk.from} has {renameAsk.sessions} logged session{renameAsk.sessions === 1 ? '' : 's'}. Is {renameAsk.to} the same movement?
            </div>
            <Button t={t} variant="primary" size="lg" style={{ minHeight: 48, justifyContent: 'center' }} onClick={() => {
              // Moves the logs (and plan, programs, swaps, meta) to the new name and keeps an alias.
              setState(prev => renameExerciseEverywhere(prev, renameAsk.from, renameAsk.to));
              setRenameAsk(null);
            }}>
              Same movement, carry history
            </Button>
            <Button t={t} size="lg" style={{ minHeight: 48, justifyContent: 'center' }} onClick={() => {
              updateExercise(renameAsk.dayIdx, renameAsk.exId, { name: renameAsk.to });
              setRenameAsk(null);
            }}>
              New exercise, fresh history
            </Button>
            <Button t={t} variant="ghost" style={{ minHeight: 44, justifyContent: 'center' }} onClick={() => setRenameAsk(null)}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      {picker && (
        <ExercisePicker t={t} isDark={isDark} aliases={state.exerciseAliases}
          currentName={picker.exId && !picker.alt ? state.plan[picker.dayIdx]?.exercises.find(e => e.id === picker.exId)?.name : ''}
          customExercises={state.customExercises || []} {...customEx}
          onPick={name => {
            if (picker.alt) {
              addAlt(picker.dayIdx, picker.exId, name);
            } else if (picker.exId) {
              renameExercise(picker.dayIdx, picker.exId, name);
            } else {
              addExerciseWithName(picker.dayIdx, name);
            }
          }}
          onClose={() => setPicker(null)} />
      )}
    </div>
  );
}
